import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Award, GraduationCap, BookMarked, Heart, CheckCircle2, ArrowRight, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";

const TEACHER_IMG = "https://images.pexels.com/photos/4173338/pexels-photo-4173338.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const CERTS = [
  { title: "IMSE Orton-Gillingham Certified Educator", note: "Comprehensive + Advanced + Kindergarten levels" },
  { title: "CERI Certified Structured Literacy Dyslexia Interventionist", note: "Center for Effective Reading Instruction" },
  { title: "LETRS Trained", note: "Language Essentials for Teachers of Reading & Spelling" },
  { title: "Member, International Dyslexia Association", note: "dyslexiaida.org" },
  { title: "M.Ed. Intervention Specialist K-12", note: "Antioch McGregor — Mild to Moderate" },
  { title: "Ohio K-12 Reading Endorsement", note: "Antioch McGregor" },
  { title: "Ohio Principal License K-12", note: "Indiana Wesleyan" },
  { title: "DIBELS NEXT / Acadience Trained", note: "+ AimswebPlus, NWEA MAP, CTOP, PAST" },
  { title: "B.A. Political Science / History", note: "Hanover College" },
];

const TIMELINE = [
  { year: "2009 – Present", role: "Intervention Specialist & Title 1 Reading Specialist", place: "Franklin City Schools, OH" },
  { year: "2020 – Present", role: "Private Tutor — Reading Disabilities", place: "Springboro, OH" },
  { year: "2017 – 2020", role: "Tutor for Reading Disabilities", place: "Sugarcreek Educational Concepts" },
  { year: "2021", role: "Finalist — David Gregory Excellence in Teaching Award", place: "Recognition" },
  { year: "2013", role: "Nominated — Project Excellence Teacher Award", place: "Recognition" },
];

export default function About() {
  return (
    <main className="bg-cream">
      {/* HERO */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 py-20 lg:py-24" data-testid="about-hero">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="lg:col-span-7">
            <span className="text-xs uppercase tracking-[0.18em] text-terracotta font-semibold">Meet Your Tutor</span>
            <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-ink text-balance">
              Hi, I'm Jonna Chrisman.
            </h1>
            <p className="mt-6 text-lg text-ink/75 leading-relaxed">
              For 17+ years I've spent my days teaching kids who struggle to read — first as a Title 1 Reading
              Specialist and Intervention Specialist in Ohio public schools, and now in a private practice where I get to give each child
              the time and approach they actually need.
            </p>
            <p className="mt-4 text-base text-ink/70 leading-relaxed">
              I believe two things deeply: <span className="text-ink font-medium">All kids can learn to read</span>, and{" "}
              <span className="text-ink font-medium">kids rise to clear expectations</span>. My sessions are
              firm but fun — high pace, real wins, and the structured-literacy backbone that the research has shown
              over and over again actually works for kids with dyslexia and ADHD.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <Link to="/contact" data-testid="about-cta-waitlist">
                <Button className="rounded-full bg-terracotta hover:bg-terracotta-600 text-white h-12 px-7 shadow-md hover:-translate-y-0.5 transition-all">
                  Join the Waitlist <ArrowRight size={18} className="ml-1.5" />
                </Button>
              </Link>
              <a href="https://www.linkedin.com/in/jonna-chrisman-b5b41929/" target="_blank" rel="noreferrer" data-testid="about-linkedin">
                <Button variant="outline" className="rounded-full border-2 border-ink text-ink hover:bg-ink hover:text-cream h-12 px-7">
                  View LinkedIn <ExternalLink size={16} className="ml-1.5" />
                </Button>
              </a>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }} className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -top-6 -right-6 w-32 h-32 bg-mustard-100 rounded-full" aria-hidden="true" />
              <div className="absolute -bottom-6 -left-6 w-24 h-24 bg-sage-100 rounded-2xl rotate-6" aria-hidden="true" />
              <img src={TEACHER_IMG} alt="Jonna working with a student" className="relative w-full h-[520px] object-cover rounded-[2rem] shadow-xl" data-testid="about-jonna-photo" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section className="bg-white py-20 lg:py-24" data-testid="about-philosophy">
        <div className="mx-auto max-w-5xl px-5 sm:px-8 lg:px-12">
          <span className="text-xs uppercase tracking-[0.18em] text-terracotta font-semibold">Philosophy</span>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-ink text-balance">
            Firm but fun. Always evidence-based.
          </h2>
          <div className="mt-10 grid sm:grid-cols-2 gap-6">
            {[
              { i: Heart, t: "Kids first", s: "Every child wants to be a reader. My job is to make sure they get the tools they need — and feel proud while doing it." },
              { i: BookMarked, t: "Science of Reading", s: "I teach the way decades of research tell us the brain learns to read. No guessing, no whole-language shortcuts." },
              { i: GraduationCap, t: "Data-driven", s: "Every plan starts with a careful assessment. We measure what works and adjust." },
              { i: Award, t: "Coordinated", s: "I work alongside parents, classroom teachers, and IEP teams when relevant — because reading wins live everywhere." },
            ].map(({ i: Icon, t, s }) => (
              <div key={t} className="rounded-2xl bg-cream p-7 border border-ink/5">
                <div className="h-11 w-11 rounded-2xl bg-terracotta text-white flex items-center justify-center"><Icon size={20} /></div>
                <h3 className="mt-4 font-display text-xl font-semibold text-ink">{t}</h3>
                <p className="mt-2 text-sm text-ink/70 leading-relaxed">{s}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CREDENTIALS */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 py-20 lg:py-24" data-testid="about-credentials">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <span className="text-xs uppercase tracking-[0.18em] text-terracotta font-semibold">Credentials</span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-ink text-balance">
              The training behind every session.
            </h2>
            <p className="mt-5 text-ink/70 leading-relaxed">
              Reading intervention works when it's done right. These are the certifications, degrees, and trainings
              that shape Jonna's practice.
            </p>
          </div>
          <div className="lg:col-span-8">
            <ul className="grid sm:grid-cols-2 gap-4">
              {CERTS.map((c) => (
                <li key={c.title} className="rounded-2xl bg-white border border-ink/5 p-5 flex gap-3" data-testid={`cred-${c.title.replace(/\s+/g, '-').toLowerCase()}`}>
                  <CheckCircle2 size={18} className="text-sage-600 flex-none mt-0.5" />
                  <div>
                    <div className="font-medium text-ink text-sm">{c.title}</div>
                    <div className="text-xs text-ink/60 mt-0.5">{c.note}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* TIMELINE */}
      <section className="bg-ink text-cream py-20 lg:py-24" data-testid="about-timeline">
        <div className="mx-auto max-w-5xl px-5 sm:px-8 lg:px-12">
          <span className="text-xs uppercase tracking-[0.18em] text-mustard font-semibold">Experience</span>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-cream text-balance">
            17+ years inside the classroom and one-on-one.
          </h2>
          <ol className="mt-12 space-y-6">
            {TIMELINE.map((t) => (
              <li key={t.role} className="grid sm:grid-cols-12 gap-3 sm:gap-6 items-start border-l-2 border-cream/15 sm:border-0 pl-4 sm:pl-0">
                <div className="sm:col-span-3 text-mustard font-medium text-sm">{t.year}</div>
                <div className="sm:col-span-9">
                  <div className="font-display text-lg text-cream">{t.role}</div>
                  <div className="text-sm text-cream/60 mt-0.5">{t.place}</div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>
    </main>
  );
}
